import { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/feature/ComicCard';
import JsonLd from '@/components/base/JsonLd';
import { categories, hotComics, newComics, recommendedComics } from '@/mocks/comics';

const heroBg = 'https://readdy.ai/api/search-image?query=A%20stylized%20artistic%20abstract%20illustration%20with%20soft%20warm%20gradients%20of%20coral%20orange%20and%20cream%20white%2C%20gentle%20circular%20geometric%20shapes%20floating%2C%20minimalist%20modern%20design%2C%20dreamy%20atmosphere%2C%20smooth%20flowing%20lines%2C%20elegant%20and%20clean%20composition%2C%20no%20text%2C%20no%20people%2C%20warm%20vibrant%20color%20palette&width=1920&height=900&seq=hero-bg-main&orientation=landscape';

export default function Home() {
  const [activeTab, setActiveTab] = useState<'hot' | 'new'>('hot');

  const displayComics = activeTab === 'hot' ? hotComics : newComics;

  return (
    <>
      <JsonLd
        data={{
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: '天天漫画网',
          url: 'https://www.tiantianmanhua.com',
          description: '天天漫画网主打下拉式完整版免费阅读，无删减韩国漫画资源充足，下拉观看流畅便捷',
          sameAs: ['https://www.tiantianmanhua.com'],
        }}
      />
      <JsonLd
        data={{
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          name: '天天漫画网',
          url: 'https://www.tiantianmanhua.com',
          description: '下拉式完整版免费阅读，无删减韩国漫画免费阅读平台',
          potentialAction: {
            '@type': 'SearchAction',
            target: 'https://www.tiantianmanhua.com/search?q={search_term_string}',
            'query-input': 'required name=search_term_string',
          },
        }}
      />
      <JsonLd
        data={{
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: [
            { '@type': 'Question', name: '天天漫画网是什么？', acceptedAnswer: { '@type': 'Answer', text: '天天漫画网是一个专注于韩国漫画（Webtoon）的在线免费阅读平台，主打下拉式完整版无删减漫画阅读体验，资源丰富、更新及时，完全免费。' } },
            { '@type': 'Question', name: '所有漫画都是免费的吗？', acceptedAnswer: { '@type': 'Answer', text: '是的！天天漫画网上的所有漫画均完全免费阅读，无需任何付费订阅或购买会员。' } },
            { '@type': 'Question', name: '漫画是完整版无删减的吗？', acceptedAnswer: { '@type': 'Answer', text: '我们对所有漫画都坚持提供完整版内容，绝无任何删减或修改。' } },
            { '@type': 'Question', name: '什么是下拉式阅读？', acceptedAnswer: { '@type': 'Answer', text: '下拉式阅读是一种适合手机和网页的漫画阅读模式，画面纵向排列，您只需向下滑动即可连续阅读，无需翻页。' } },
            { '@type': 'Question', name: '漫画更新频率如何？', acceptedAnswer: { '@type': 'Answer', text: '天天漫画网与各大韩国漫画平台保持同步更新，热门连载漫画通常每日或隔日更新最新章节。' } },
          ],
        }}
      />
      <Header />
      <main className="min-h-screen">
        {/* Hero Section */}
        <section className="relative w-full h-[520px] md:h-[620px] overflow-hidden">
          <img
            src={heroBg}
            alt="天天漫画网 - 下拉式完整版免费阅读"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/20 to-background-50"></div>
          <div className="absolute inset-0 flex flex-col items-center justify-center px-4">
            <div className="w-full max-w-3xl text-center">
              <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-4 leading-tight">
                下拉式完整版免费阅读
              </h1>
              <p className="text-base md:text-lg text-white/80 mb-8 max-w-xl mx-auto leading-relaxed">
                无删减韩国漫画资源充足，下拉观看流畅便捷，<br className="hidden sm:block" />
                让您每天都能免费解锁完整版韩国漫画的新鲜剧情
              </p>
              <div className="flex items-center justify-center gap-3 flex-wrap">
                <Link
                  to="/category/romance"
                  className="px-8 py-3 rounded-full bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
                >
                  开始阅读
                </Link>
                <Link
                  to="/about"
                  className="px-8 py-3 rounded-full bg-white/15 text-white font-semibold text-sm hover:bg-white/25 transition-colors backdrop-blur-sm whitespace-nowrap"
                >
                  了解更多
                </Link>
              </div>
              <div className="flex items-center justify-center gap-8 mt-10 flex-wrap">
                <div className="text-center">
                  <p className="text-2xl md:text-3xl font-bold text-white">2000+</p>
                  <p className="text-xs text-white/60 mt-1">漫画资源</p>
                </div>
                <div className="w-px h-10 bg-white/20"></div>
                <div className="text-center">
                  <p className="text-2xl md:text-3xl font-bold text-white">每日更新</p>
                  <p className="text-xs text-white/60 mt-1">稳定连载</p>
                </div>
                <div className="w-px h-10 bg-white/20"></div>
                <div className="text-center">
                  <p className="text-2xl md:text-3xl font-bold text-white">完全免费</p>
                  <p className="text-xs text-white/60 mt-1">无删减完整版</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Category Navigation */}
        <section className="max-w-[1400px] mx-auto px-4 md:px-8 -mt-8 relative z-10">
          <div className="bg-background-50 rounded-2xl px-6 py-5 flex items-center justify-center gap-2 flex-wrap"
            style={{
              boxShadow: '0 2px 20px rgba(0,0,0,0.04)',
            }}
          >
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium text-foreground-700 hover:text-foreground-950 hover:bg-background-100 transition-all whitespace-nowrap"
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className={`${cat.icon} text-base`}></i>
                </span>
                {cat.name}
              </Link>
            ))}
          </div>
        </section>

        {/* Hot & New Tabs */}
        <section className="max-w-[1400px] mx-auto px-4 md:px-8 mt-14">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950">
                {activeTab === 'hot' ? '热门推荐' : '最新上架'}
              </h2>
              <p className="text-sm text-foreground-600 mt-1">
                {activeTab === 'hot' ? '大家都在看的火爆漫画' : '刚刚更新的新鲜内容'}
              </p>
            </div>
            <div className="flex items-center gap-1 bg-background-100 rounded-full p-1">
              <button
                onClick={() => setActiveTab('hot')}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                  activeTab === 'hot'
                    ? 'bg-primary-500 text-background-50'
                    : 'text-foreground-600 hover:text-foreground-950'
                }`}
              >
                热门
              </button>
              <button
                onClick={() => setActiveTab('new')}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                  activeTab === 'new'
                    ? 'bg-primary-500 text-background-50'
                    : 'text-foreground-600 hover:text-foreground-950'
                }`}
              >
                最新
              </button>
            </div>
          </div>

          {displayComics.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5">
              {displayComics.slice(0, 12).map((comic) => (
                <ComicCard key={comic.id} comic={comic} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <p className="text-foreground-500">暂无内容</p>
            </div>
          )}

          <div className="flex justify-center mt-8">
            <Link
              to={activeTab === 'hot' ? '/category/romance' : '/category/fantasy'}
              className="px-8 py-3 rounded-full border-2 border-foreground-200 text-sm font-medium text-foreground-700 hover:border-foreground-400 hover:text-foreground-950 transition-all whitespace-nowrap"
            >
              查看更多
            </Link>
          </div>
        </section>

        {/* Recommended Section */}
        <section className="max-w-[1400px] mx-auto px-4 md:px-8 mt-16">
          <div className="mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950">编辑精选</h2>
            <p className="text-sm text-foreground-600 mt-1">每周精选优质韩国漫画，不容错过</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5">
            {recommendedComics.slice(0, 8).map((comic) => (
              <ComicCard key={comic.id} comic={comic} />
            ))}
          </div>
        </section>

        {/* Features Banner */}
        <section className="max-w-[1400px] mx-auto px-4 md:px-8 mt-16 mb-16">
          <div className="bg-gradient-to-r from-accent-500/10 via-background-100 to-primary-500/10 rounded-2xl px-8 py-12 text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
              为什么选择天天漫画网？
            </h2>
            <p className="text-sm text-foreground-600 mb-10 max-w-lg mx-auto">
              专注韩国漫画，为您提供最优质的在线阅读体验
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { icon: 'ri-shield-check-line', title: '无删减完整版', desc: '所有漫画均为完整版内容，绝无任何删减' },
                { icon: 'ri-smartphone-line', title: '下拉式阅读', desc: '流畅的下拉式浏览体验，更适合手机观看' },
                { icon: 'ri-flashlight-line', title: '极速更新', desc: '每日同步最新章节，让你追番不再等待' },
                { icon: 'ri-vip-crown-line', title: '完全免费', desc: '全部漫画免费阅读，无需任何付费订阅' },
              ].map((item) => (
                <div key={item.title} className="text-center">
                  <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-2xl bg-primary-500 text-background-50 mb-4">
                    <i className={`${item.icon} text-2xl`}></i>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-950 mb-2">{item.title}</h3>
                  <p className="text-xs text-foreground-600 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="bg-background-100 py-16">
          <div className="max-w-[1400px] mx-auto px-4 md:px-8">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950">常见问题</h2>
              <p className="text-sm text-foreground-600 mt-1">关于天天漫画网，您可能想了解这些</p>
            </div>
            <div className="max-w-3xl mx-auto space-y-3">
              {[
                { q: '天天漫画网是什么？', a: '天天漫画网是一个专注于韩国漫画（Webtoon）的在线免费阅读平台，主打下拉式完整版无删减漫画阅读体验，资源丰富、更新及时，完全免费。' },
                { q: '所有漫画都是免费的吗？', a: '是的！天天漫画网上的所有漫画均完全免费阅读，无需任何付费订阅或购买会员。我们致力于为漫友提供最优质的免费阅读体验。' },
                { q: '漫画是完整版无删减的吗？', a: '我们对所有漫画都坚持提供完整版内容，绝无任何删减或修改。您可以放心享受原汁原味的韩国漫画内容。' },
                { q: '什么是下拉式阅读？', a: '下拉式阅读是一种适合手机和网页的漫画阅读模式，画面纵向排列，您只需向下滑动即可连续阅读，无需翻页，浏览体验更加流畅。' },
                { q: '漫画更新频率如何？', a: '天天漫画网与各大韩国漫画平台保持同步更新，热门连载漫画通常每日或隔日更新最新章节，让您追番不再等待。' },
              ].map((faq, idx) => (
                <details key={idx} className="bg-background-50 rounded-xl p-5 group cursor-pointer">
                  <summary className="text-sm font-semibold text-foreground-950 list-none flex items-center justify-between">
                    {faq.q}
                    <span className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                      <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
                    </span>
                  </summary>
                  <p className="text-sm text-foreground-600 mt-3 leading-relaxed">{faq.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}